'use client';

// Package Imports
import React, { FunctionComponent, useState, ChangeEvent, FormEvent, useEffect } from 'react';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

// Action Imports
import { AddToCart } from '@/lib/functions/addToCart';

// Component Imports
import StylizedButton from '@/components/stylizedButton';
import Input from '@/components/input';

// Context Imports
import { useOrder } from '@/lib/context/orderContext';

// Constants Imports
import { Product } from '@/lib/types/products';
import { AddItemToOrderResponse } from '@/lib/types/addItemToOrderResponse';

// Style Imports
import styles from './addToCartForm.module.scss';

// Define Interfaces
interface AddToCartProps extends Product {
  label?: string;
  caseQuantity?: number;
  hideQuantityField?: boolean;
  classNameWrapper?: string;
  classNameInput?: string;
  classNameButton?: string;
  showToast?: (type: 'error' | 'success' | 'notice', message: string) => void;
  disabled?: boolean;
}

/**
 * AddToCartForm Component
 *
 * This component renders a form to add a product to the shopping cart.
 * It includes an input for quantity and a button to submit the form.
 */
const AddToCartForm: FunctionComponent<AddToCartProps> = ({
  title,
  caseQuantity,
  code,
  hideQuantityField = false,
  label = 'ADD TO CART',
  classNameWrapper = '',
  classNameButton = '',
  classNameInput = '',
  showToast,
  disabled = false,
}): React.ReactElement => {
  const [quantity, setQuantity] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isClient, setIsClient] = useState<boolean>(false);
  const inputId = uuidv4();
  const { state: orderState, dispatch: orderDispatch } = useOrder();

  useEffect(() => {
    setIsClient(true);
    setIsLoading(false);
  }, []);

  /**
   * Handles changes in the quantity input field.
   * Ensures the quantity is a positive integer.
   *
   * @param e - The change event from the input field.
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const quantityValue = parseInt(value, 10);
    if (!isNaN(quantityValue) && quantityValue > 0) {
      setQuantity(quantityValue);
    } else {
      setQuantity(1);
    }
  };

  /**
   * Handles the form submission.
   * Calls the AddToCart function with the SKU and quantity.
   *
   * @param e - The form submission event.
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (disabled) return;

    if (quantity > 0 && code) {
      setIsLoading(true);

      // Determine if single product or case
      let finalQuantity = quantity;
      if (caseQuantity) {
        finalQuantity = quantity * caseQuantity;
      }
      
      try {
        const res: AddItemToOrderResponse = await AddToCart(code, finalQuantity, orderState, orderDispatch);
        console.log('AddToCart response:', res);
        if (res && res.success) {
          if (showToast) {
            const message = `${quantity} × "${title}" has been added to your cart.`;
            showToast('success', message);
          }
        } else {
          if (showToast) {
            if (res?.responseData?.errorType === 'out-of-stock') {
              showToast('error', `"${title}" could not be added to your cart as there is insuffient stock at your selected store.`);
            } else {
              showToast('error', `${quantity} × "${title}" could not be added to your cart.`);
            }
          }
        }
      } catch (error) {
        console.error('Error adding to cart:', error);
        if (showToast) {
          showToast('error', `${quantity} × "${title}" could not be added to your cart.`);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      console.error('Quantity must be greater than 0');
      if (showToast) {
        showToast('error', 'Quantity must be greater than 0');
      }
    }
  };

  return (
    <form
      className={classNames([
        styles.form,
        { [classNameWrapper]: classNameWrapper },
      ])}
      onSubmit={handleSubmit}
    >
      {isClient && (
        <>
          {!hideQuantityField && (
            <>
              <label className="screenReaderText" htmlFor={inputId}>
                quantity
              </label>
              <Input
                classname={classNames([
                  styles.quantityInput,
                  { [classNameInput]: classNameInput },
                ])}
                type="number"
                id={inputId}
                name={inputId}
                value={quantity}
                onChange={handleChange}
                disabled={isLoading || disabled}
              />
            </>
          )}
          <StylizedButton
            className={classNames([
              styles.button,
              { [classNameButton]: classNameButton },
            ])}
            handleClick={handleSubmit}
            disabled={!isClient || isLoading || disabled}
            loading={isLoading}
            size="mini"
          >
            {label}
          </StylizedButton>
        </>
      )}
    </form>
  );
};

export default AddToCartForm;
